import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from "date-fns"

export type CalendarView = "day" | "week" | "month"

export interface CalendarFetchRange {
  start: Date
  end: Date
}

/**
 * The exact date range the calendar grid fetches (`betweenDates`), given the
 * view's anchor date. For month view this is the visible 6-week grid (Monday of
 * the week containing the 1st → Sunday of the week containing the last day);
 * for day/week views it's the Monday→Sunday week containing the anchor.
 *
 * Kept in sync with the `queryStart`/`queryEnd` derivation in jobs-new-page and
 * reused by the route loader / prefetch helpers so they warm the identical
 * query key.
 */
export function calendarFetchRangeFromAnchor(
  view: CalendarView,
  anchorStart: Date,
): CalendarFetchRange {
  const start = startOfWeek(anchorStart, { weekStartsOn: 1 })
  const end =
    view === "month"
      ? endOfDay(endOfWeek(endOfMonth(anchorStart), { weekStartsOn: 1 }))
      : endOfDay(addDays(start, 6))
  return { start, end }
}

/**
 * Resolves the view's anchor start date from the route's `startDate` search
 * param (falling back to `today`), matching the component's `initialStart`.
 */
export function calendarAnchorStart(
  view: CalendarView,
  startDate: string | undefined,
  today: Date,
): Date {
  const base = startDate ? new Date(startDate) : today
  return view === "month" ? startOfMonth(base) : base
}

/**
 * Convenience: the fetch range straight from route params + today. Used by the
 * loader and prefetch helpers.
 */
export function calendarFetchRange(
  view: CalendarView,
  startDate: string | undefined,
  today: Date,
): CalendarFetchRange {
  return calendarFetchRangeFromAnchor(
    view,
    calendarAnchorStart(view, startDate, today),
  )
}
